<template>
  <div class="si-panel">
    <div class="si-title">{{ $t("invitePanel.title") }}</div>
    <div class="si-sub">
      {{ $t("invitePanel.sub") }}
    </div>

    <v-form ref="form" v-model="valid" class="mt-4" @submit.prevent="submit">
      <v-row dense>
        <v-col cols="12" md="4">
          <div class="si-label">{{ $t("invitePanel.level") }}</div>
          <v-select
            v-model="form.level"
            :items="levelOptions"
            :rules="[rules.required]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('invitePanel.select')"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div class="si-label">{{ $t("invitePanel.modality") }}</div>
          <v-select
            v-model="form.format"
            :items="formatOptions"
            :rules="[rules.required]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('invitePanel.select')"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div class="si-label">{{ $t("invitePanel.frequency") }}</div>
          <v-select
            v-model="form.frequency"
            :items="frequencyOptions"
            :rules="[rules.required]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('invitePanel.select')"
          />
        </v-col>
      </v-row>

      <div class="si-label mt-1">
        {{ $t("invitePanel.dayTime") }}
        <span v-if="slotHint" class="si-hint">— {{ slotHint }}</span>
      </div>
      <v-row
        v-for="(slot, index) in form.schedules"
        :key="index"
        dense
        align="center"
      >
        <v-col cols="12" md="5">
          <v-select
            v-model="slot.weekDay"
            :items="weekDayOptions"
            :rules="[rules.requiredNumber]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('invitePanel.weekDay')"
          />
        </v-col>
        <v-col cols="5" md="3">
          <v-select
            v-model="slot.startHour"
            :items="hourOptions"
            :rules="[rules.required]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('invitePanel.start')"
            @change="syncEnd(slot)"
          />
        </v-col>
        <v-col cols="5" md="3">
          <v-select
            v-model="slot.endHour"
            :items="endHourOptions(slot)"
            :rules="[rules.required]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('invitePanel.end')"
          />
        </v-col>
        <v-col cols="2" md="1" class="text-center">
          <v-btn
            v-if="form.schedules.length > minSlots"
            icon
            small
            :aria-label="$t('invitePanel.removeDay')"
            @click="form.schedules.splice(index, 1)"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-btn
        v-if="canAddSlot"
        text
        small
        color="#A826E1"
        class="px-0 mb-2"
        @click="addSlot"
      >
        <v-icon small left>mdi-plus</v-icon>{{ $t("invitePanel.addDay") }}
      </v-btn>

      <!-- Card 17trt6jcrw3: dupla, trio ou quarteto aceitam um e-mail por
           aluno. Cada um recebe o próprio link e todos entram na mesma turma. -->
      <div class="si-label">
        {{
          maxStudents > 1 ? $t("invitePanel.emails") : $t("invitePanel.email")
        }}
        <span v-if="maxStudents > 1" class="si-hint">
          {{ $t("invitePanel.emailsHint", { count: maxStudents }) }}
        </span>
      </div>
      <v-row
        v-for="(item, index) in form.emails"
        :key="item.key"
        dense
        align="center"
      >
        <v-col cols="10" md="7">
          <v-text-field
            v-model="item.value"
            :rules="[rules.required, rules.email, rules.uniqueEmail]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('groupInvite.emailPlaceholder')"
            type="email"
          />
        </v-col>
        <v-col cols="2" md="1" class="text-center">
          <v-btn
            v-if="form.emails.length > 1"
            icon
            small
            :aria-label="$t('invitePanel.removeStudent')"
            class="si-remove"
            @click="form.emails.splice(index, 1)"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="8" class="d-flex align-center">
          <v-btn
            v-if="form.emails.length < maxStudents"
            text
            small
            color="#A826E1"
            class="px-0 mb-2"
            @click="addEmail"
          >
            <v-icon small left>mdi-plus</v-icon
            >{{ $t("invitePanel.addStudent") }}
          </v-btn>
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-btn
            block
            depressed
            color="#A826E1"
            class="white--text si-btn"
            :loading="saving"
            type="submit"
          >
            <v-icon small left>mdi-link-variant</v-icon
            >{{
              form.emails.length > 1
                ? $t("invitePanel.generateMany")
                : $t("invitePanel.generateOne")
            }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <v-alert
      v-if="created.length"
      type="success"
      text
      dense
      class="mt-2"
      icon="mdi-check-circle-outline"
    >
      <template v-if="created.length > 1">
        {{ $t("invitePanel.createdMany") }}
      </template>
      <template v-else>{{ $t("invitePanel.createdOne") }}</template>
      {{ $t("invitePanel.copyToo") }}
      <div v-for="invite in created" :key="invite.id" class="mt-2">
        <b>{{ invite.email }}</b>
        <div class="si-link">
          <span class="si-link__text">{{ invite.link }}</span>
          <v-btn small depressed @click="copy(invite.link)">
            <v-icon small left>mdi-content-copy</v-icon>{{ $t("common.copy") }}
          </v-btn>
        </div>
      </div>
    </v-alert>

    <student-invite-list
      class="mt-6"
      :invites="invites"
      :loading="loadingList"
      @cancel="cancel"
      @copy="copy"
    />
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import { hours } from "@/shared/hours";
import {
  ENGLISH_LEVELS,
  GROUP_FORMAT_OPTIONS,
  GROUP_FREQUENCY_OPTIONS,
  WEEK_DAY_ITEMS,
} from "@/shared/classPresentation";
import StudentInviteList from "@/components/connect/partner/StudentInviteList.vue";

// Card 86akng14v: aba "Vincular aluno" do perfil do professor.
// Quantos dias a frequencia pede. Quinzenal (4) e UM dia -- nunca usar o valor
// do enum como contagem (mesma regra de perWeekToRequiredSlots na API).
const REQUIRED_SLOTS = { 1: 1, 2: 2, 3: 3, 4: 1 };

const emptySlot = () => ({ weekDay: null, startHour: null, endHour: null });
let emailKey = 0;
const emptyEmail = () => ({ key: ++emailKey, value: "" });

export default {
  name: "StudentInvitePanel",
  components: { StudentInviteList },
  data() {
    return {
      valid: false,
      saving: false,
      loadingList: false,
      created: [],
      invites: [],
      form: {
        level: null,
        format: null,
        frequency: null,
        emails: [emptyEmail()],
        schedules: [emptySlot()],
      },
      rules: {
        required: (v) =>
          (v !== null && v !== "") || this.$t("common.requiredFieldDot"),
        requiredNumber: (v) =>
          (v !== null && v !== undefined) || this.$t("common.requiredFieldDot"),
        email: (v) =>
          /.+@.+\..+/.test(v || "") || this.$t("common.invalidEmail"),
        uniqueEmail: (v) =>
          this.form.emails.filter(
            (item) =>
              item.value.trim().toLowerCase() === (v || "").trim().toLowerCase()
          ).length <= 1 || this.$t("invitePanel.duplicateEmail"),
      },
    };
  },
  computed: {
    levelOptions() {
      return ENGLISH_LEVELS;
    },
    formatOptions() {
      return GROUP_FORMAT_OPTIONS;
    },
    frequencyOptions() {
      return GROUP_FREQUENCY_OPTIONS.filter((option) =>
        [1, 2, 3, 4].includes(option.value)
      );
    },
    weekDayOptions() {
      return WEEK_DAY_ITEMS;
    },
    hourOptions() {
      return hours;
    },
    // Modalidade = quantos alunos cabem na turma (Individual 1 .. Quarteto 4).
    maxStudents() {
      return Number(this.form.format) || 1;
    },
    minSlots() {
      return REQUIRED_SLOTS[this.form.frequency] || 1;
    },
    canAddSlot() {
      if (+this.form.frequency === 3) return this.form.schedules.length < 7;
      return this.form.schedules.length < this.minSlots;
    },
    slotHint() {
      if (!this.form.frequency) return "";
      if (+this.form.frequency === 3) return this.$t("invitePanel.hintThree");
      if (+this.form.frequency === 4) {
        return this.$t("invitePanel.hintBiweekly");
      }
      return this.minSlots === 1
        ? this.$t("invitePanel.hintOne")
        : this.$t("invitePanel.hintMany", { count: this.minSlots });
    },
  },
  watch: {
    maxStudents(value) {
      if (this.form.emails.length > value) this.form.emails.splice(value);
    },
    "form.frequency"(value) {
      const required = REQUIRED_SLOTS[value] || 1;
      const slots = this.form.schedules;
      if (+value !== 3 && slots.length > required) {
        slots.splice(required);
      }
      while (slots.length < required) slots.push(emptySlot());
    },
  },
  created() {
    this.loadInvites();
  },
  methods: {
    addEmail() {
      this.form.emails.push(emptyEmail());
    },
    addSlot() {
      this.form.schedules.push(emptySlot());
    },
    endHourOptions(slot) {
      // A ultima aula comeca 22:30: o fim sugerido (uma hora depois) e 23:30.
      const extended = [...hours, "23:00", "23:30"];
      if (!slot.startHour) return extended;
      return extended.filter((hour) => hour > slot.startHour);
    },
    syncEnd(slot) {
      if (!slot.startHour) return;
      if (!slot.endHour || slot.endHour <= slot.startHour) {
        const [h, m] = slot.startHour.split(":").map(Number);
        slot.endHour = `${String(h + 1).padStart(2, "0")}:${String(m).padStart(
          2,
          "0"
        )}`;
      }
    },
    async loadInvites() {
      this.loadingList = true;
      try {
        const res = await partnerApi.listStudentInvites();
        this.invites = res.data?.data || [];
      } catch (error) {
        this.invites = [];
      } finally {
        this.loadingList = false;
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;

      const days = this.form.schedules.map((slot) => slot.weekDay);
      if (new Set(days).size !== days.length) {
        this.alert(this.$t("invitePanel.oneSlotPerDay"), 1);
        return;
      }

      const emails = this.form.emails.map((item) => item.value.trim());

      this.saving = true;
      this.created = [];
      try {
        const res = await partnerApi.createStudentInvite({
          ...(emails.length > 1 ? { emails } : { email: emails[0] }),
          level: +this.form.level,
          format: +this.form.format,
          frequency: +this.form.frequency,
          schedules: this.form.schedules.map((slot) => ({
            weekDay: +slot.weekDay,
            startHour: slot.startHour,
            endHour: slot.endHour,
          })),
        });
        this.created = res.data?.invites || [res.data];
        this.form.emails = [emptyEmail()];
        this.$refs.form.resetValidation();
        this.alert(
          this.created.length > 1
            ? this.$t("invitePanel.generatedMany")
            : this.$t("groupInvite.generated"),
          0
        );
        this.loadInvites();
      } catch (error) {
        this.alert(this.errorText(error), 1);
      } finally {
        this.saving = false;
      }
    },
    async cancel(invite) {
      try {
        await partnerApi.cancelStudentInvite(invite.id);
        this.created = this.created.filter((item) => item.id !== invite.id);
        this.alert(this.$t("groupInvite.canceled"), 0);
        this.loadInvites();
      } catch (error) {
        this.alert(this.errorText(error), 1);
      }
    },
    async copy(link) {
      try {
        await navigator.clipboard.writeText(link);
        this.alert(this.$t("common.linkCopied"), 0);
      } catch (error) {
        this.alert(this.$t("common.copyFailed"), 2);
      }
    },
    errorText(error) {
      const raw = error?.response?.data?.message;
      const message = Array.isArray(raw) ? raw[0] : raw;
      return message || this.$t("groupInvite.generateFailed");
    },
    alert(text, type) {
      this.$root.$emit("display-alert", { text, type });
    },
  },
};
</script>

<style scoped>
.si-title {
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--dk-ink, #1f1147);
}
.si-sub {
  color: var(--dk-muted, #6b6480);
  font-size: 0.9rem;
  margin-top: 4px;
}
.si-label {
  font-size: 0.85rem;
  margin-bottom: 4px;
  color: var(--dk-ink-soft, #3d3653);
}
.si-hint {
  color: var(--dk-muted, #8a84a0);
}
.si-btn {
  border-radius: 8px;
  text-transform: none;
  margin-bottom: 26px;
}
.si-link {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.si-link__text {
  word-break: break-all;
  font-family: monospace;
  font-size: 0.85rem;
}
</style>
